import React, { useContext, useEffect, useMemo } from 'react';
import { t } from '@lingui/macro';
import { useBreakpoint } from '@landr/maestro';
import { useSearchController } from 'controllers/SearchController/SearchContext';
import { useSearchModifiers } from 'controllers/SearchController/hooks/useSearchModifiers';
import { useCurrentSearchState } from 'controllers/SearchController/hooks/useCurrentSearchState';
import useHasFilterApplied from './hooks/useHasFilterApplied';
import useSuggestions from './hooks/useSuggestions';
import useInput from './hooks/useInput';
import useSelectSuggestion from './hooks/useSelectSuggestion';
import usePredictiveInput from './hooks/usePredictiveInput';
import useKeyEvent from './hooks/useKeyEvents';
import useSearchBar from './hooks/useSearchBar';
import { SearchBarContextType } from './types';
import { useLabels } from './hooks/useLabels';
import { usePacks } from './hooks/usePacks';
import { useSuggestionsFromFilters } from './hooks/useSuggestionsFromFilters';
import { useGetRecentOrTrending } from './hooks/useGetRecentOrTrending';
import { DefaultSearchBarContext } from './default';

export const SearchBarContext = React.createContext<SearchBarContextType>(
    DefaultSearchBarContext,
);

export const useSearchBarContext = (): SearchBarContextType =>
    useContext(SearchBarContext);

export const SearchBarProvider = ({
    children,
    defaultPlaceholder = t`Search instruments, genres, keyword...`,
    showOnlyFilterSuggestions,
    isRecentAllowed = false,
    isTrendingAllowed = false,
}: {
    children: React.ReactNode;
    defaultPlaceholder?: string;
    showOnlyFilterSuggestions: boolean;
    isRecentAllowed?: boolean;
    isTrendingAllowed?: boolean;
}): React.ReactElement => {
    const isMobile = useBreakpoint('md');
    const hasFilterApplied = useHasFilterApplied({});
    const { load: loadLabels } = useLabels();
    const { load: loadPacks } = usePacks();

    useEffect(() => {
        loadLabels();
    }, [loadLabels]);

    useEffect(() => {
        loadPacks();
    }, [loadPacks]);

    const { hasActiveFilters } = useCurrentSearchState();

    const {
        activeTabTotal,
        samplesTotal,
        filterInstrumentsMeta,
        filterGenresMeta,
        filterSfxsMeta,
        filterTagsMeta,
    } = useSearchController();

    const {
        addFilter,
        removeFilter: searchControllerRemoveFilter,
        clearAll,
    } = useSearchModifiers();

    const {
        inputValue,
        hasTextInputValue,
        clearTextInput,
        placeholder,
        onInputChange,
    } = useInput({
        defaultPlaceholder,
        hasFilterApplied,
    });

    const suggestionsFromFilters = useSuggestionsFromFilters({
        samplesTotal,
        filterInstrumentsMeta,
        filterGenresMeta,
        filterSfxsMeta,
        filterTagsMeta,
    });

    const { suggestions: autocompleteSuggestions } = useSuggestions({
        searchInput: inputValue,
        hasActiveSearch: hasActiveFilters,
        showOnlyFilterSuggestions,
        suggestionsFromFilters,
    });

    const isInputEmpty = !inputValue && !hasFilterApplied;

    const { isRecentOrTrendingVisible, recentTrending } =
        useGetRecentOrTrending({
            showOnlyFilterSuggestions,
            isInputEmpty,
            isRecentAllowed,
            isTrendingAllowed,
        });

    const suggestions = useMemo(
        () =>
            isRecentOrTrendingVisible
                ? recentTrending
                : autocompleteSuggestions,
        [isRecentOrTrendingVisible, recentTrending, autocompleteSuggestions],
    );

    const {
        selectPreviousSuggestion,
        selectNextSuggestion,
        selectedSuggestionIndex,
        resetSelectedSuggestion,
    } = useSelectSuggestion({
        suggestions,
    });

    const { predictiveModifiers, predictiveInput } = usePredictiveInput({
        inputValue,
        selectedSuggestionIndex,
        suggestions,
    });

    const {
        inputRef,
        applySuggestion,
        removeFilter,
        clearCtaLabel,
        handleClearCta,
        handleFocus,
        closeSuggestions,
        isSuggestionsOpen,
        handleApplyTextSearch,
    } = useSearchBar({
        addFilter,
        searchControllerRemoveFilter,
        clearAll,
        clearTextInput,
        hasTextInputValue,
        isMobile,
        resetSelectedSuggestion,
        inputValue,
        predictiveModifiers,
    });

    const { handleKeyPress, handleKeyDown } = useKeyEvent({
        onInputChange,
        predictiveInput,
        selectPreviousSuggestion,
        selectNextSuggestion,
        handleApplyTextSearch,
    });

    const isSearchBarDisabled = activeTabTotal === 0 && !hasActiveFilters;

    const context = {
        clearCtaLabel,
        hasFilterApplied,
        hasSuggestions: suggestions.length !== 0,
        hasTextInputValue,
        inputRef,
        inputValue,
        isModal: isMobile,
        isSuggestionsOpen,
        isSearchBarDisabled,
        isTrendingVisible: isInputEmpty && isTrendingAllowed,
        placeholder,
        predictiveInput,
        selectedSuggestionIndex,
        suggestions,
        suggestionsFromFilters,
        applySuggestion,
        closeSuggestions,
        handleApplyTextSearch,
        handleClearCta,
        handleFocus,
        handleKeyDown,
        handleKeyPress,
        onInputChange,
        removeFilter,
    };

    return (
        <SearchBarContext.Provider value={context}>
            {children}
        </SearchBarContext.Provider>
    );
};

export default SearchBarProvider;
